import { useEffect, useMemo, useState } from '@web-stories-wp/react';

const loadedFontKeys = new Set();

const getFontKey = ({ family, url, format, weight, style }) =>
  [family, url, format, weight, style].join('|');

const hashString = (value) => {
  let hash = 0;

  for (let index = 0; index < value.length; index++) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash).toString(36);
};

const escapeCssString = (value) =>
  String(value).replace(/\\/g, '\\\\').replace(/'/g, "\\'");

const ensureFontFaceRule = (font) => {
  const key = getFontKey(font);
  const elementId = `web-stories-lottie-font-${hashString(key)}`;

  if (document.getElementById(elementId)) {
    return;
  }

  const style = document.createElement('style');
  style.id = elementId;
  style.textContent = `@font-face {
    font-family: '${escapeCssString(font.family)}';
    src: url('${escapeCssString(font.url)}') format('${escapeCssString(
    font.format
  )}');
    font-style: ${font.style};
    font-weight: ${font.weight};
    font-display: swap;
  }`;
  document.head.appendChild(style);
};

const loadLottieFont = async (font) => {
  const key = getFontKey(font);

  if (loadedFontKeys.has(key)) {
    return;
  }

  ensureFontFaceRule(font);

  if (document.fonts?.load) {
    const fontFaceSet = `${font.style} ${font.weight} 16px '${font.family}'`;
    await document.fonts.load(fontFaceSet);
  }

  loadedFontKeys.add(key);
};

export const loadLottieFonts = (fonts) =>
  Promise.all(fonts.map(loadLottieFont));

function useLoadLottieFonts(fonts = []) {
  const fontKey = useMemo(() => JSON.stringify(fonts), [fonts]);
  const [loaded, setLoaded] = useState(() => fonts.length === 0);

  useEffect(() => {
    let cancelled = false;
    const parsedFonts = JSON.parse(fontKey);
    const normalizedFonts = Array.isArray(parsedFonts) ? parsedFonts : [];

    setLoaded(normalizedFonts.length === 0);

    if (normalizedFonts.length === 0) {
      return () => {
        cancelled = true;
      };
    }

    loadLottieFonts(normalizedFonts)
      .catch(() => null)
      .then(() => {
        if (!cancelled) {
          setLoaded(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fontKey]);

  return loaded;
}

export default useLoadLottieFonts;
